import React, { useState, useEffect } from "react";
import {
  useLazyGetAdminsQuery,
  useCreateAdminMutation,
  useUpdateAdminMutation,
  useDeleteAdminMutation,
} from "../api/businessApi";
import Swal from "sweetalert2";
import AdminModal from "./AdminModal";

const AdminManagement = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [getAdmins, { data: admins, isLoading, error }] =
    useLazyGetAdminsQuery();
  const [createAdmin] = useCreateAdminMutation();
  const [updateAdmin] = useUpdateAdminMutation();
  const [deleteAdmin, { isDeleting }] = useDeleteAdminMutation();

  useEffect(() => {
    getAdmins();
  }, [getAdmins]);

  const handleSave = async (formData) => {
    try {
      if (editingAdmin) {
        await updateAdmin({ id: editingAdmin.id, ...formData }).unwrap();
        Swal.fire("Success!", "Admin updated", "success");
      } else {
        await createAdmin(formData).unwrap();
        Swal.fire("Success!", "Admin created", "success");
      }
      setShowModal(false);
      getAdmins();
    } catch (error) {
      console.log(error);
      Swal.fire("Error!", error.data?.message || "Operation failed", "error");
    }
  };

  const handleDelete = async (admin) => {
    const result = await Swal.fire({
      title: "Delete Admin?",
      text: `Remove ${admin.username}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });

    if (result.isConfirmed) {
      try {
        await deleteAdmin(admin.id).unwrap();
        Swal.fire("Deleted!", "", "success");
        getAdmins();
      } catch (error) {
        Swal.fire("Error!", "Delete failed", "error");
      }
    }
  };

  const filteredAdmins = (admins || []).filter(
    (admin) =>
      !searchTerm ||
      admin.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#135879]"></div>
      </div>
    );
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-700">
          Error loading Admins: {error.message || "Unknown error"}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Admin Management</h2>
        <button
          onClick={() => {
            setEditingAdmin(null);
            setShowModal(true);
          }}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded"
        >
          Add Admin
        </button>
      </div>

      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full p-2 mb-4 border rounded"
        placeholder="Search admins..."
      />

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Username
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Role
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredAdmins.map((admin) => (
              <tr key={admin.id}>
                <td className="px-6 py-4">{admin.username}</td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                    {admin.role}
                  </span>
                </td>
                <td className="px-6 py-4 flex items-center justify-end gap-3 text-right text-sm font-medium">
                  <button
                    onClick={() => {
                      setEditingAdmin(admin);
                      setShowModal(true);
                    }}
                    className="text-purple-600 hover:text-purple-900 flex items-center gap-2"
                    title="Edit business"
                  >
                    <svg
                      className="w-4 h-4 inline"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      ></path>
                    </svg>
                    Edit
                  </button>
                  <button
                    disabled={isDeleting}
                    onClick={() => handleDelete(admin)}
                    className="text-red-600 flex items-center gap-2 hover:text-red-900"
                    title="Delete business"
                  >
                    <svg
                      className="w-4 h-4 inline"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      ></path>
                    </svg>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AdminModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        admin={editingAdmin}
        onSave={handleSave}
        isLoading={false}
      />
    </div>
  );
};

export default AdminManagement;
